/**
 * A mapping of branch codes to their corresponding constant values.
 * These codes represent different sectors within the transportation industry.
 */
export const branchCodes = {
  TFFLYG: "28CD80F7-FF07-DE11-B4E1-02BF9E69CA65",
  TFHAMN: "13CD80F7-FF07-DE11-B4E1-02BF9E69CA65",
  TFMOTOR: "1DCD80F7-FF07-DE11-B4E1-02BF9E69CA65",
  TFSJOFART: "31CD80F7-FF07-DE11-B4E1-02BF9E69CA65",
  TFVAG: "19CD80F7-FF07-DE11-B4E1-02BF9E69CA65",
  TFBUSS: "2CCD80F7-FF07-DE11-B4E1-02BF9E69CA65",
  TFINDUSTRI: "3DCD80F7-FF07-DE11-B4E1-02BF9E69CA65"
} as const;

export const extendedBranch = {
  TFALL: "ALL"
} as const;
/**
 * Type alias for branch codes.
 */

export const extendedBranchCodeArray = [
  ...Object.keys(branchCodes),
  ...Object.keys(extendedBranch)
] as ExtendedBranchCodeArrayType[];

export type ExtendedBranchCodeArrayType =
  | keyof typeof extendedBranch
  | keyof typeof branchCodes;

export type BranchCode = keyof typeof branchCodes;

export type BranchGuid = (typeof branchCodes)[BranchCode];
/**
 * Type alias for extended branch codes, including a generic "ALL" category.
 */
export type ExtendedBranchCode = BranchCode | "TFALL";

/**
 * Type definition for mapping branch codes to their icon file paths.
 */
export type BranchIcons = { [K in BranchCode]: string };

/**
 * Type definition for mapping branch codes to their names.
 */
export type BranchNames = { [K in BranchCode]: string | string[] };

/**
 * Extended type definition for branch icons, including the generic "ALL" category.
 */
export type ExtendedBranchIcons = BranchIcons & { TFALL: string };

/**
 * Extended type definition for branch names, including the generic "ALL" category.
 */
export type ExtendedBranchNames = BranchNames & { TFALL: string };

/**
 * Object mapping each branch code to its corresponding icon path.
 */
export const branchIcons: BranchIcons = {
  TFINDUSTRI: "fa-solid fa-truck-moving",
  TFFLYG: "fa-solid fa-plane-up",
  TFHAMN: "fa-solid fa-anchor",
  TFMOTOR: "fa-solid fa-car",
  TFSJOFART: "fa-solid fa-ship",
  TFVAG: "fa-solid fa-road",
  TFBUSS: "fa-solid fa-bus-simple"
};

/**
 * Object mapping each branch code to its name.
 */
export const branchNames: BranchNames = {
  TFFLYG: "Transportföretagen Flyg",
  TFHAMN: "Transportföretagen Hamn",
  TFMOTOR: "Transportföretagen Motor",
  TFSJOFART: "Transportföretagen Sjöfart",
  TFVAG: "Transportföretagen Väg",
  TFBUSS: "Sveriges Bussföretag",
  TFINDUSTRI: "Transportindustriförbundet"
};

export const branchNamesExtended: BranchNames = {
  TFFLYG: ["Transportföretagen Flyg", "Svenska Flygbranschen"],
  TFHAMN: ["Transportföretagen Hamn", "Sveriges Hamnar"],
  TFMOTOR: ["Transportföretagen Motor", "Motorbranchens Arbetsgivareförbund"],
  TFSJOFART: ["Transportföretagen Sjöfart", "Sjöfartens Arbetsgivareförbund"],
  TFVAG: ["Transportföretagen Väg", "Sveriges Transportindustriförbund"],
  TFBUSS: "Sveriges Bussföretag",
  TFINDUSTRI: "Transportindustriförbundet"
};

export const getBranchIconByName = (name: string) => {
    return Object.keys(branchNamesExtended).find(
        (key) => Array.isArray(branchNamesExtended[key]) ? branchNamesExtended[key].includes(name) : branchNamesExtended[key] === name
    );
}

/**
 * Detailed type for branch information including code, icon, and name.
 */
export type BranchDetails = {
  code: string;
  icon: BranchCode | string;
  name: string | string[];
};

/**
 * Constructs an object mapping each branch code to detailed information
 * about the branch, including its code, icon path, and name.
 */
export const branchesDetails: { [K in BranchCode]: BranchDetails } =
  Object.keys(branchCodes).reduce(
    (details, code) => {
      const key = code as BranchCode;
      details[key] = {
        code: branchCodes[key],
        icon: branchIcons[key],
        name: branchNames[key]
      };
      return details;
    },
    {} as { [K in BranchCode]: BranchDetails }
  );

/**
 * A flag indicating whether to include a generic "ALL" category in the branch details.
 */
const includeAllBranch = true; // This can be dynamic based on your application's logic.

/**
 * Extended version of branchesDetails to include "TFALL" if includeAllBranch is true.
 */
export const extendedBranchesDetails: Record<
  ExtendedBranchCode,
  BranchDetails
> = Object.keys(branchCodes).reduce(
  (details, code) => {
    const key = code as BranchCode;
    details[key] = {
      code: branchCodes[key],
      icon: key,
      name: branchNames[key]
    };
    return details;
  },
  {} as Record<ExtendedBranchCode, BranchDetails>
);

if (includeAllBranch) {
  extendedBranchesDetails.TFALL = {
    code: "",
    icon: "TFALL",
    name: "Alla branscher"
  };
}

/**
 * Exports the extended branches details as an array of objects for easy iteration and display.
 */
export const extendedBranchesDetailsArray = Object.values(
  extendedBranchesDetails
);

export const branchCodesArray = Object.keys(branchCodes) as BranchCode[];
export const tfall = Object.keys(extendedBranch) as ExtendedBranchCode[];

export const newBranchCodes = [...branchCodesArray, ...tfall];
export const branchGuidsArray = Object.values(branchCodes) as BranchGuid[];
export const getBrancCodeFromGuid = (guid) =>
  Object.keys(branchCodes).find(
    (key) => branchCodes[key] === guid.toUpperCase()
  );
